import { useState } from "react";

import { accessTrackName } from "@/access/tracks";
import { ConsentType } from "@/api/generated/models";
import { useUpdateConsents } from "@/api/mutations/consent";
import {
  Adapt,
  BulletedList,
  Button,
  Dialog,
  Footer,
  H1,
  Header,
  Heading,
  Main,
  Paragraph,
  ScrollView,
  Sheet,
} from "@/components";
import { useUserProgression } from "@/conversation/sidebar/UserProgression/UserProgressionContext";

/** Confirms a decline before it ends the enrollment, since it closes the track's progression. */
const DeclineConfirmation = ({
  open,
  programName,
  isPending,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  programName: string;
  isPending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) => (
  <Dialog open={open} onOpenChange={(isOpen: boolean) => !isOpen && onCancel()}>
    <Adapt when="maxLg">
      <Sheet
        modal
        open={open}
        onOpenChange={(isOpen: boolean) => !isOpen && onCancel()}
        snapPointsMode="fit"
        disableDrag
      >
        <Sheet.Frame padding="$300" gap="$100">
          <Adapt.Contents />
        </Sheet.Frame>
        <Sheet.Overlay
          backgroundColor="$fillOverlay"
          backdropFilter="blur(8px)"
        />
      </Sheet>
    </Adapt>
    <Dialog.Portal>
      <Dialog.Overlay
        backgroundColor="$fillOverlay"
        backdropFilter="blur(8px)"
      />
      <Dialog.Content
        borderRadius="$150"
        padding="$300"
        minWidth={400}
        maxWidth={500}
        gap="$100"
      >
        <Dialog.Title asChild>
          <H1 size="$xl" $lg={{ size: "$xl" }}>
            Are you sure you want to stop your enrollment?
          </H1>
        </Dialog.Title>
        <Paragraph size="$sm" paddingVertical="$100">
          {`We'll close your ${programName} enrollment. You can start it again any time.`}
        </Paragraph>
        <Footer marginTop="$200">
          <Button
            dataTrackingId="cms-consent-decline-cancel-button"
            mode="primary"
            onPress={onCancel}
            disabled={isPending}
          >
            No, continue my enrollment
          </Button>
          <Button
            dataTrackingId="cms-consent-decline-confirm-button"
            mode="tertiary"
            onPress={onConfirm}
            disabled={isPending}
            loading={isPending}
          >
            Yes, cancel
          </Button>
        </Footer>
      </Dialog.Content>
    </Dialog.Portal>
  </Dialog>
);

/**
 * CMS requires an explicit, informed opt-in before we enroll a patient in an ACCESS track.
 * Declining is recorded too, so the ask is repeated if they come back to enroll later.
 */
export const CMSConsentScreen = ({
  onContinue,
  onDecline,
}: {
  onContinue: () => void;
  onDecline: () => void;
}) => {
  const flowName = useUserProgression()?.progression.flow_name;
  const trackName = flowName ? accessTrackName(flowName) : undefined;
  const programName = trackName ? `ACCESS ${trackName}` : "ACCESS";
  const [confirmingDecline, setConfirmingDecline] = useState(false);
  const { mutate: updateConsents, isPending } = useUpdateConsents();

  const recordConsent = (accepted: boolean, onRecorded: () => void) =>
    updateConsents(
      { data: { consents: { [ConsentType.cms_access_consent]: accepted } } },
      { onSuccess: onRecorded },
    );

  return (
    <>
      <ScrollView
        flex={1}
        contentContainerStyle={{
          paddingHorizontal: "$200",
          paddingBottom: "$400",
          gap: "$200",
        }}
      >
        <Header paddingTop="$100">
          <Heading size="$xl">You are eligible for ACCESS</Heading>
        </Header>
        <Main gap="$100">
          <Paragraph>
            {`You are signing up for the ${programName} program offered by Curai Health.`}
          </Paragraph>
          <BulletedList
            items={[
              "Participation in ACCESS is voluntary.",
              "You can leave or switch after 90 days.",
              "You can only participate in one ACCESS program track per provider at this time.",
            ]}
            icon={<BulletedList.Bullet />}
            size="$md"
            rootProps={{ gap: "$50" }}
          />
        </Main>
      </ScrollView>
      <Footer padding="$200" mode="dialog">
        <Button
          dataTrackingId="cms-consent-enroll-button"
          mode="primary"
          constrained
          onPress={() => recordConsent(true, onContinue)}
          disabled={isPending}
          loading={isPending}
        >
          Yes, I want to enroll
        </Button>
        <Button
          dataTrackingId="cms-consent-do-not-enroll-button"
          mode="secondary"
          constrained
          onPress={() => setConfirmingDecline(true)}
          disabled={isPending}
        >
          Don't want to enroll
        </Button>
      </Footer>
      <DeclineConfirmation
        open={confirmingDecline}
        programName={programName}
        isPending={isPending}
        onCancel={() => setConfirmingDecline(false)}
        onConfirm={() => recordConsent(false, onDecline)}
      />
    </>
  );
};
